import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Unstable_Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import ListItemText from '@mui/material/ListItemText';

import PremiumGuard from 'src/auth/guard/premium-guard';
import {
  useGetAllRfmQuery,
  UpdateRfmConfigMutationVariables,
  useUpdateRfmConfigMutation as useUpdateRfmConfig,
} from 'src/graphql/generated';

import FormProvider from 'src/components/hook-form';
import { useSnackbar } from 'src/components/snackbar';

import { CardValueForm } from './components/card-value-form';
import RfmAutomationSettings from './rfm-automation-settings';
import { CardRecencyForm } from './components/card-recency-form';
import { CardFrequencyForm } from './components/card-frequency-form';
import AcquisitionChannelsConfig from './acquisition-channels-config';
import { RfmFormSchema, toRfmBandInput, rfmFormSchemaDefaultValues } from './rfm-form-schema';

// ----------------------------------------------------------------------

export default function ConfigCustomerTab() {
  const [enableForm, setEnableForm] = useState<boolean>(false);
  const { data: rfmData, loading, error, refetch: refetchAll } = useGetAllRfmQuery();

  const { enqueueSnackbar } = useSnackbar();
  const [updateRfm] = useUpdateRfmConfig();

  const methods = useForm({
    resolver: yupResolver(RfmFormSchema),
    defaultValues: rfmFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const updateFields = () => {
    if (!rfmData) return;

    const getCutoffs = (dimension: 'recency' | 'frequency' | 'value') => {
      const scale = dimension === 'value' ? 100 : 1;
      return {
        zoneOneMax: Math.round(Number(rfmData[dimension]?.zoneOneMax ?? 0) * scale),
        zoneTwoMax: Math.round(Number(rfmData[dimension]?.zoneTwoMax ?? 0) * scale),
        zoneThreeMax: Math.round(Number(rfmData[dimension]?.zoneThreeMax ?? 0) * scale),
        zoneFourMax: Math.round(Number(rfmData[dimension]?.zoneFourMax ?? 0) * scale),
      };
    };
    reset({
      recency: getCutoffs('recency'),
      frequency: getCutoffs('frequency'),
      value: getCutoffs('value'),
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const variables: UpdateRfmConfigMutationVariables = {
        recency: toRfmBandInput(data.recency),
        frequency: toRfmBandInput(data.frequency),
        value: toRfmBandInput(data.value, true),
      };

      await updateRfm({ variables });

      enqueueSnackbar(
        'Faixas RFM salvas. A classificação será atualizada no próximo processamento.',
        { variant: 'success' }
      );

      await refetchAll();

      setEnableForm(false);
    } catch (saveError) {
      if (saveError?.message) {
        enqueueSnackbar(saveError?.message, { variant: 'error' });

        return;
      }

      enqueueSnackbar('Não foi possível atualizar os dados!', { variant: 'error' });
    }
  });

  useEffect(() => {
    updateFields();
    // eslint-disable-next-line
  }, [rfmData]);

  const renderRecencyCardForm = (
    <Grid container spacing={3}>
      <Grid xs={12} md={4}>
        <ListItemText
          primary="Recência"
          secondary={
            <>
              Dias desde a última compra válida do cliente. Comprar novamente melhora a recência.
              <br />
              <br />
              Quanto menos dias, maior a nota: 5 é a melhor e 1 é a menor.
            </>
          }
          primaryTypographyProps={{ typography: 'h6', mb: 0.5 }}
          secondaryTypographyProps={{ component: 'span' }}
        />
      </Grid>

      <Grid xs={12} md={8}>
        <CardRecencyForm enableForm={enableForm} />
      </Grid>
    </Grid>
  );

  const renderFrequencyCardForm = (
    <Grid container spacing={3}>
      <Grid xs={12} md={4}>
        <ListItemText
          primary="Frequência"
          secondary={
            <>
              Quantidade de compras válidas em todo o histórico do cliente. Novas compras aumentam a
              frequência.
              <br />
              <br />
              Quanto mais compras, maior a nota: 5 é a melhor e 1 é a menor.
            </>
          }
          primaryTypographyProps={{ typography: 'h6', mb: 0.5 }}
          secondaryTypographyProps={{ component: 'span' }}
        />
      </Grid>

      <Grid xs={12} md={8}>
        <CardFrequencyForm enableForm={enableForm} />
      </Grid>
    </Grid>
  );

  const renderValueCardForm = (
    <Grid container spacing={3}>
      <Grid xs={12} md={4}>
        <ListItemText
          primary="Valor monetário"
          secondary={
            <>
              Soma do valor de todas as compras válidas do cliente, na moeda da loja. Este é o total
              acumulado, não o ticket médio.
              <br />
              <br />
              Quanto maior o valor, maior a nota: 5 é a melhor e 1 é a menor.
            </>
          }
          primaryTypographyProps={{ typography: 'h6', mb: 0.5 }}
          secondaryTypographyProps={{ component: 'span' }}
        />
      </Grid>

      <Grid xs={12} md={8}>
        <CardValueForm enableForm={enableForm} />
      </Grid>
    </Grid>
  );

  return (
    <Stack spacing={3}>
      <PremiumGuard permissionKey="hasRfm">
        <Stack spacing={3}>
          <RfmAutomationSettings />
          <FormProvider methods={methods}>
            <Grid container spacing={3}>
              <Grid xs={12} gap={3} display="flex" flexDirection="column">
                <ListItemText
                  primary="Faixas da matriz RFM"
                  secondary="Defina os limites que representam o comportamento dos seus clientes. As notas vão de 1 a 5 e, juntas, determinam a categoria. As faixas são contínuas; a última não tem limite máximo. Compras canceladas e com data futura não entram no cálculo."
                  primaryTypographyProps={{ typography: 'h5', mb: 0.5, component: 'h2' }}
                  secondaryTypographyProps={{ component: 'span' }}
                  sx={{
                    pr: 3,
                    pl: 3,
                  }}
                />
                <Stack component={Card} spacing={3} sx={{ p: 3 }}>
                  {loading && <Skeleton variant="rounded" height={160} />}
                  {error && (
                    <Alert
                      severity="error"
                      action={<Button onClick={() => refetchAll()}>Tentar novamente</Button>}
                    >
                      Não foi possível carregar as faixas RFM.
                    </Alert>
                  )}
                  {!loading && !error && (
                    <>
                      {renderRecencyCardForm}
                      {renderFrequencyCardForm}
                      {renderValueCardForm}
                    </>
                  )}
                  {enableForm && (
                    <Stack
                      flexDirection="row"
                      justifyContent="flex-end"
                      alignItems="flex-end"
                      gap={2}
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        disabled={isSubmitting}
                        onClick={() => {
                          updateFields();
                          setEnableForm(false);
                        }}
                      >
                        Cancelar
                      </Button>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        color="primary"
                        onClick={onSubmit}
                      >
                        Salvar
                      </LoadingButton>
                    </Stack>
                  )}
                  {!enableForm && !loading && !error && (
                    <Stack display="flex" flexDirection="row" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setEnableForm(true)}
                      >
                        Editar
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
              <Grid xs={12}>
                <AcquisitionChannelsConfig />
              </Grid>
            </Grid>
          </FormProvider>
        </Stack>
      </PremiumGuard>
    </Stack>
  );
}
